import {
  AcademicCapIcon,
  CheckBadgeIcon,
  StarIcon,
  TrophyIcon,
} from "@heroicons/react/24/solid";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { getUserData, purchaseHearts } from "../utils/userStorage.ts";

type LessonType = "regular" | "practice" | "final";

type LessonBoxProps =
  | {
      type: LessonType;
      state: "on-progress";
      colorClassName?: string;
      currentLesson: number;
      totalLessons: number;
      lessonTitle: string;
      lessonDescription: string;
      lessonId: string;
    }
  | {
      type: LessonType;
      state: "not-started" | "finish";
      colorClassName?: string;
      currentLesson?: number;
      totalLessons?: number;
      lessonTitle: string;
      lessonDescription: string;
      lessonId: string;
    };

const getIcon = (type: LessonType, state: string, darkMode: boolean) => {
  if (state === "not-started") {
    const iconClass = darkMode
      ? "w-10 h-10 text-gray-600"
      : "w-10 h-10 text-gray-400";
    switch (type) {
      case "regular":
        return <StarIcon className={iconClass} />;
      case "practice":
        return <AcademicCapIcon className={iconClass} />;
      case "final":
        return <TrophyIcon className={iconClass} />;
    }
  }
  if (type === "final" && state === "finish") {
    return <TrophyIcon className="w-10 h-10 text-yellow-500 z-10" />;
  }
  if (state === "finish" || state === "on-progress") {
    const iconClass = "w-10 h-10 text-white z-10";
    if (type === "practice" || type === "regular") {
      return state === "finish" ? (
        <CheckBadgeIcon className={iconClass} />
      ) : type === "practice" ? (
        <AcademicCapIcon className={iconClass} />
      ) : (
        <StarIcon className={iconClass} />
      );
    }
    if (type === "final") {
      return <TrophyIcon className={iconClass} />;
    }
  }
  return null;
};

const colorMap: Record<string, string> = {
  "bg-red-500": "#ef4444",
  "bg-blue-500": "#3b82f6",
  "bg-green-500": "#22c55e",
  "bg-purple-500": "#a855f7",
  "bg-pink-500": "#ec4899",
  "bg-orange-500": "#f97316",
  "bg-teal-500": "#14b8a6",
};

type LessonBoxPropsWithDark = LessonBoxProps & { darkMode?: boolean };

export const LessonBox: React.FC<LessonBoxPropsWithDark> = ({
  type,
  state,
  colorClassName,
  currentLesson,
  totalLessons,
  lessonTitle,
  lessonDescription,
  lessonId,
  darkMode = false,
}) => {
  // Modal for heart 0
  const [showHeartModal, setShowHeartModal] = useState(false);

  // Listen for userData changes
  useEffect(() => {
    const handleUserDataChange = () => {
      setUserData(getUserData());
    };
    window.addEventListener("userDataChanged", handleUserDataChange);
    return () =>
      window.removeEventListener("userDataChanged", handleUserDataChange);
  }, []);
  const [popupPos, setPopupPos] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [userData, setUserData] = useState(() => getUserData());
  const [showPopup, setShowPopup] = useState(false);
  const [popupClosing, setPopupClosing] = useState(false);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const isAutoScrollingRef = useRef(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close popup on scroll if open
  useEffect(() => {
    isAutoScrollingRef.current = isAutoScrolling;
  }, [isAutoScrolling]);

  useEffect(() => {
    if (!showPopup) return;
    const handleScroll = () => {
      // Only close if not auto-scrolling (always use latest value)
      if (showPopup && !isAutoScrollingRef.current) closePopup();
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [showPopup]);

  const ringColor = colorMap[colorClassName || "bg-red-500"] || "#ef4444";

  // Shimmer effect for finish state
  const shimmer = (
    <span className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl">
      <span
        className="absolute left-[-60%] top-0 w-[120%] h-full"
        style={{
          background:
            "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.25) 50%, transparent 70%)",
          animation: "shimmer-move 6.2s infinite",
        }}
      />
      <style>
        {`
          @keyframes shimmer-move {
            0% { left: -60%; }
            19.35% { left: 100%; }
            100% { left: 100%; }
          }
        `}
      </style>
    </span>
  );

  // Progress ring for on-progress state
  const safeProgress =
    typeof currentLesson === "number" &&
    typeof totalLessons === "number" &&
    totalLessons > 0
      ? Math.max(0, Math.min(1, currentLesson / totalLessons))
      : 0;

  const progressRing =
    state === "on-progress"
      ? (() => {
          const size = 112;
          const box = 96;
          const r = 20;
          const s = 8;
          const path = `
          M ${size / 2},${s}
          H ${size - s - r}
          Q ${size - s},${s} ${size - s},${s + r}
          V ${size - s - r}
          Q ${size - s},${size - s} ${size - s - r},${size - s}
          H ${s + r}
          Q ${s},${size - s} ${s},${size - s - r}
          V ${s + r}
          Q ${s},${s} ${s + r},${s}
          H ${size / 2}
        `;
          const pathLength = 4 * (box - 2 * r) + 2 * Math.PI * r;
          const placeholderColor = darkMode ? "#374151" : "#d1d5db"; // gray-700 for dark, gray-300 for light
          return (
            <svg
              className="absolute -top-4 -left-4 w-28 h-28 z-20"
              width={size}
              height={size}
            >
              <path
                d={path}
                fill="none"
                stroke={placeholderColor}
                strokeWidth="4"
                strokeLinecap="round"
              />
              <path
                d={path}
                fill="none"
                stroke={ringColor}
                strokeWidth="4"
                strokeDasharray={pathLength}
                strokeDashoffset={pathLength * (1 - safeProgress)}
                strokeLinecap="round"
                style={{ transition: "stroke-dashoffset 0.5s" }}
              />
            </svg>
          );
        })()
      : null;

  // Box color
  const boxColor =
    state === "not-started"
      ? darkMode
        ? "bg-gray-800 border border-gray-700"
        : "bg-gray-200"
      : colorClassName || "bg-red-500";

  // Show popup above box, and scroll if needed to avoid BottomNavBar
  const handleShowPopup = () => {
    if (
      (state === "not-started" ||
        state === "on-progress" ||
        state === "finish") &&
      boxRef.current
    ) {
      const rect = boxRef.current.getBoundingClientRect();
      const popupWidth = window.innerWidth <= 767 ? 280 : 340;
      const popupHeight = window.innerWidth <= 767 ? 220 : 240; // estimate
      const popupTop = rect.top - 24 + 120; // same as before
      const popupBottom = popupTop + popupHeight;
      const viewportHeight = window.innerHeight;
      const bottomNavBarHeight = 130;
      // If pop-up would overlap BottomNavBar, scroll so it's fully visible
      if (popupBottom > viewportHeight - bottomNavBarHeight) {
        // Scroll so the pop-up bottom is above the BottomNavBar
        const scrollY =
          window.scrollY +
          (popupBottom - (viewportHeight - bottomNavBarHeight)) +
          16;
        setIsAutoScrolling(true);
        window.scrollTo({ top: scrollY, behavior: "smooth" });
        setTimeout(() => {
          setIsAutoScrolling(false);
          // Recalculate box position after scroll
          if (boxRef.current) {
            const newRect = boxRef.current.getBoundingClientRect();
            setPopupPos({
              left: newRect.left + newRect.width / 2,
              top: newRect.top - 24,
            });
          }
        }, 500); // duration matches scroll animation
        setShowPopup(true);
      } else {
        setPopupPos({
          left: rect.left + rect.width / 2,
          top: rect.top - 24,
        });
        setShowPopup(true);
      }
    }
  };

  // Update close logic
  const closePopup = () => {
    setPopupClosing(true);
    setTimeout(() => {
      setShowPopup(false);
      setPopupClosing(false);
    }, 250); // match animation duration
  };

  // Pop-up content with expand/shrink animation
  const popupContent =
    popupPos && showPopup ? (
      <>
        {/* Overlay: click to close */}
        <div
          className="fixed inset-0 bg-black bg-opacity-0 z-[9998]"
          onClick={closePopup}
        />
        {/* Pop-up card */}
        {window.innerWidth <= 767 ? (
          <div
            className={`fixed ${boxColor} rounded-xl shadow-lg pt-12 pb-6 px-6 mt-2 flex flex-col items-center transition-all duration-400 ${
              popupClosing
                ? "opacity-0 scale-0 pointer-events-none"
                : "opacity-100 scale-100"
            } ${
              state === "not-started" && darkMode
                ? "border border-gray-700"
                : ""
            }`}
            style={{
              zIndex: 9999,
              width: 280,
              left: "50%",
              top: popupPos.top + 120,
              transform: "translateX(-50%)",
              background:
                state === "not-started" && darkMode ? "#1f2937" : undefined,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Bubble tail at dynamic horizontal position (mobile only) */}
            {(() => {
              const popupWidth = 280;
              const popupCenter = window.innerWidth / 2;
              const boxCenter = popupPos.left;
              let tailLeft = popupWidth / 2 + (boxCenter - popupCenter);
              tailLeft = Math.max(16, Math.min(popupWidth - 16, tailLeft));
              return (
                <svg
                  className="absolute -top-3"
                  style={{ left: tailLeft - 16 }}
                  width="32"
                  height="16"
                  viewBox="0 0 32 16"
                >
                  <path
                    d="M16 0 Q20 16 32 16 Q16 12 0 16 Q12 16 16 0"
                    fill={
                      state === "not-started"
                        ? darkMode
                          ? "#1f2937"
                          : "#e5e7eb"
                        : colorMap[boxColor] || "#ef4444"
                    }
                  />
                </svg>
              );
            })()}
            <div
              className={`${
                state === "not-started"
                  ? darkMode
                    ? "text-gray-300"
                    : "text-gray-500"
                  : "text-white"
              } text-lg text-center mb-1`}
              style={{ fontFamily: "Feather Bold" }}
            >
              {lessonTitle}
            </div>
            <div
              className={`${
                state === "not-started"
                  ? darkMode
                    ? "text-gray-400"
                    : "text-gray-500"
                  : "text-white"
              } text-center text-base font-medium mb-6`}
              style={{ fontFamily: "DIN Next Rounded" }}
            >
              {state === "on-progress"
                ? `Pelajaran ${
                    typeof currentLesson === "number" ? currentLesson + 1 : 0
                  } dari ${typeof totalLessons === "number" ? totalLessons : 5}`
                : state === "not-started"
                ? "Selesaikan semua level di atas untuk membuka ini!"
                : lessonDescription}
            </div>
            {state === "on-progress" ? (
              <button
                className="w-full bg-white text-gray-700 rounded-lg py-3 mb-3 shadow text-base"
                style={{ fontFamily: "Feather Bold" }}
                onClick={() => {
                  if ((userData?.meta?.heart ?? 0) === 0) {
                    setShowHeartModal(true);
                    setShowPopup(false);
                  } else {
                    navigate(`/lesson?lessonId=${lessonId}`);
                  }
                }}
              >
                MULAI +40 XP
              </button>
            ) : state === "not-started" ? (
              <button
                className={`w-full rounded-lg py-3 mb-3 shadow text-base cursor-not-allowed ${
                  darkMode
                    ? "bg-gray-700 bg-opacity-40 text-gray-400"
                    : "bg-gray-700 bg-opacity-30 text-gray-500"
                }`}
                style={{ fontFamily: "Feather Bold" }}
                disabled
              >
                TERKUNCI
              </button>
            ) : (
              <button
                className="w-full bg-white text-gray-700 rounded-lg py-3 mb-3 shadow text-base"
                style={{ fontFamily: "Feather Bold" }}
                onClick={() => {
                  if ((userData?.meta?.heart ?? 0) === 0) {
                    setShowHeartModal(true);
                    setShowPopup(false);
                  } else {
                    navigate(`/lesson?lessonId=${lessonId}`);
                  }
                }}
              >
                LATIHAN +5 XP
              </button>
            )}
            <button
              className={`absolute top-3 right-4 text-2xl font-bold ${
                state === "not-started"
                  ? darkMode
                    ? "text-gray-400"
                    : "text-gray-500"
                  : "text-white"
              }`}
              onClick={closePopup}
              aria-label="Tutup"
            >
              {"\u00D7"}
            </button>
          </div>
        ) : (
          <div
            className={`fixed ${boxColor} rounded-xl shadow-lg pt-12 pb-6 px-6 mt-2 flex flex-col items-center transition-all duration-400 ${
              popupClosing
                ? "opacity-0 scale-0 pointer-events-none"
                : "opacity-100 scale-100"
            } ${
              state === "not-started" && darkMode
                ? "border border-gray-700"
                : ""
            }`}
            style={{
              zIndex: 9999,
              width: 340,
              left: popupPos.left - 170,
              top: popupPos.top + 120,
              background:
                state === "not-started" && darkMode ? "#1f2937" : undefined,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Bubble tail at center (desktop) */}
            <svg
              className="absolute -top-3 left-1/2 -translate-x-1/2"
              width="32"
              height="16"
              viewBox="0 0 32 16"
            >
              <path
                d="M16 0 Q20 16 32 16 Q16 12 0 16 Q12 16 16 0"
                fill={
                  state === "not-started"
                    ? darkMode
                      ? "#1f2937"
                      : "#e5e7eb"
                    : colorMap[boxColor] || "#ef4444"
                }
              />
            </svg>
            <div
              className={`${
                state === "not-started"
                  ? darkMode
                    ? "text-gray-300"
                    : "text-gray-500"
                  : "text-white"
              } text-lg text-center mb-1`}
              style={{ fontFamily: "Feather Bold" }}
            >
              {lessonTitle}
            </div>
            <div
              className={`${
                state === "not-started"
                  ? darkMode
                    ? "text-gray-400"
                    : "text-gray-500"
                  : "text-white"
              } text-center text-base font-medium mb-6`}
              style={{ fontFamily: "DIN Next Rounded" }}
            >
              {state === "on-progress"
                ? `Pelajaran ${
                    typeof currentLesson === "number" ? currentLesson + 1 : 0
                  } dari ${typeof totalLessons === "number" ? totalLessons : 5}`
                : state === "not-started"
                ? "Selesaikan semua level di atas untuk membuka ini!"
                : lessonDescription}
            </div>
            {state === "on-progress" ? (
              <button
                className="w-full bg-white text-gray-700 rounded-lg py-3 mb-3 shadow text-base"
                style={{ fontFamily: "Feather Bold" }}
                onClick={() => {
                  if ((userData?.meta?.heart ?? 0) === 0) {
                    setShowHeartModal(true);
                    setShowPopup(false);
                  } else {
                    navigate(`/lesson?lessonId=${lessonId}`);
                  }
                }}
              >
                MULAI +40 XP
              </button>
            ) : state === "not-started" ? (
              <button
                className={`w-full rounded-lg py-3 mb-3 shadow text-base cursor-not-allowed ${
                  darkMode
                    ? "bg-gray-700 bg-opacity-40 text-gray-400"
                    : "bg-gray-700 bg-opacity-30 text-gray-500"
                }`}
                style={{ fontFamily: "Feather Bold" }}
                disabled
              >
                TERKUNCI
              </button>
            ) : (
              <button
                className="w-full bg-white text-gray-700 rounded-lg py-3 mb-3 shadow text-base"
                style={{ fontFamily: "Feather Bold" }}
                onClick={() => {
                  if ((userData?.meta?.heart ?? 0) === 0) {
                    setShowHeartModal(true);
                    setShowPopup(false);
                  } else {
                    navigate(`/lesson?lessonId=${lessonId}`);
                  }
                }}
              >
                LATIHAN +5 XP
              </button>
            )}
            <button
              className={`absolute top-3 right-4 text-2xl font-bold ${
                state === "not-started"
                  ? darkMode
                    ? "text-gray-400"
                    : "text-gray-500"
                  : "text-white"
              }`}
              onClick={closePopup}
              aria-label="Tutup"
            >
              {"\u00D7"}
            </button>
          </div>
        )}
      </>
    ) : null;

  return (
    <>
      <div
        ref={boxRef}
        className={`w-20 h-20 ${boxColor} rounded-xl flex items-center justify-center shadow-lg relative overflow-visible cursor-pointer duration-200 transform hover:-translate-y-0.5 transition-all ${
          state === "not-started" && darkMode ? "border border-gray-700" : ""
        }`}
        onClick={handleShowPopup}
      >
        {/* Progress ring if on-progress */}
        {progressRing}
        {/* Shimmer for finish */}
        {state === "finish" ? shimmer : null}
        {/* Icon */}
        {getIcon(type, state, darkMode)}
      </div>
      {/* Pop-up rendered in portal */}
      {popupContent ? ReactDOM.createPortal(popupContent, document.body) : null}
      {/* Heart 0 modal */}
      {showHeartModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-40">
            <div
              className={`rounded-xl shadow-lg py-8 px-6 min-w-[280px] max-w-xs text-center border transition-colors duration-200 ${
                darkMode
                  ? "bg-gray-800 border-2 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <div
                className={`text-xl font-bold mb-2 ${
                  darkMode ? "text-gray-100" : "text-gray-800"
                }`}
                style={{ fontFamily: "Feather Bold" }}
              >
                Tidak bisa mulai pelajaran
              </div>
              <div
                className={`text-md font-base mb-5 ${
                  darkMode ? "text-gray-300" : "text-gray-800"
                }`}
                style={{ fontFamily: "DIN Next Rounded" }}
              >
                Hati kamu habis. Isi ulang hati terlebih dahulu untuk memulai
                pelajaran.
              </div>
              <button
                className={`w-full px-4 py-2 rounded-lg font-bold transition text-base ${
                  darkMode
                    ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
                style={{ fontFamily: "DIN Next Rounded" }}
                onClick={() => setShowHeartModal(false)}
              >
                Mengerti
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
